<template>
  <div class="document-viewer">
    <b-overlay :show="isFetching" rounded="sm" spinner-variant="primary">
      <!-- Compact Document Generation -->
      <div class="document-generation-compact">
        <DocumentTypeGrid
          :document-types="documentTypes"
          :generating-document="generatingDocument"
          :template-documents="templateDocuments"
          :signing-template="signingTemplate"
          @generate="handleDocumentGenerate"
          @template-sign="handleTemplateSign"
          @template-upload="handleTemplateUpload"
        />
      </div>

      <!-- Gallery Actions - Only show when files are selected -->
      <GalleryActions
        v-if="selectedFiles.length > 0"
        :selected-files="selectedFiles"
        @bulk-download="handleBulkDownload"
        @bulk-delete="handleBulkDelete"
      />

      <!-- Document Categories in compact layout -->
      <div class="document-categories">
        <CategoryCard
          v-for="category in documentCategories"
          :key="category.value"
          :title="category.text"
          :category="category.value"
          :items="filteredList(category.value)"
          :selected-files="selectedFiles"
          :patient-id="patient.id"
          @upload="showUploadModal"
          @upload-success="handleUploadSuccess"
          @upload-error="handleUploadError"
          @select="toggleFileSelection"
          @preview="handlePreview"
          @download="handleDownload"
          @delete="handleDelete"
          @sign="openSignTab"
          @radiiView="openRadiiView"
        />
      </div>
    </b-overlay>

    <!-- Hidden Document Generator -->
    <DocumentGenerator
      :key="currentDocumentType"
      ref="documentGenerator"
      :document-type="currentDocumentType"
      :modal-id="`${currentDocumentType}-modal`"
      :modal-ref="`${currentDocumentType}Modal`"
      modal-class="component-modal-treatment-planning"
      style="display: none"
    />

    <!-- Lightbox for document preview -->
    <Lightbox
      :index="lightBoxIndex"
      :items="lightBoxList"
      :toolbar="[
        'counter',
        'zoom',
        'slide',
        'rotate',
        'gallery',
        'fullscreen',
        'close',
      ]"
      @close="resetLightbox"
    />

    <!-- Modals -->
    <UploadFilesModal
      :patientId="patient.id"
      ref="uploadFiles"
      @uploadedFiles="handleDocumentUploads"
      @uploadedTemplateFiles="pollForUpdatesTemplate"
    />
    <Loading v-if="isLoading" />
    <Dialog ref="Dialog" />
  </div>
</template>

<script>
import GalleryMixin from "@/mixins/GalleryMixin";
import DocumentGenerator from "@/components/modal/DocumentGenerator/DocumentGenerator.vue";
import { documentTypes } from "@/components/modal/DocumentGenerator/config/documentConfig";
import DocumentTypeGrid from "./DocumentTypeGrid.vue";
import CategoryCard from "../gallery/CategoryCard.vue";
import GalleryActions from "../gallery/GalleryActions.vue";
import UploadFilesModal from "@/components/modal/UploadFiles.vue";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading.vue";
import VueLightbox from "vue-lightbox-lite";
import "vue-lightbox-lite/dist/index.css";

export default {
  name: "DocumentViewer",
  mixins: [GalleryMixin],
  components: {
    DocumentGenerator,
    DocumentTypeGrid,
    CategoryCard,
    GalleryActions,
    UploadFilesModal,
    Dialog,
    Loading,
    Lightbox: VueLightbox,
  },
  props: {
    patient: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      currentDocumentType: "empty",
      generatingDocument: null,
      signingTemplate: null,
      documentCategories: [
        { text: "Document", value: "Document" },
        // { text: "Template Document", value: "Template" },
      ],
    };
  },
  computed: {
    documentTypes() {
      return documentTypes.map(item => {
        if (item.options) {
          return {
            ...item,
            options: item.options.map(option => ({
              ...option,
              disabled:
                option.checkForSubscription && this.isFileStorageExpired,
            })),
          };
        }
        return {
          ...item,
          disabled:
            item.disabled ||
            (item.checkForSubscription && this.isFileStorageExpired),
        };
      });
    },
    templateDocuments() {
      // Get template documents from the gallery mixin, filter to PDF only
      const templates = this.filteredList("Template") || [];
      return templates.filter(template => template.fileType === 'application/pdf');
    },
  },
  methods: {
    async showDocument() {
      try {
        await this.$nextTick();

        if (!this.$refs.documentGenerator) {
          console.warn("Document generator reference not found");
          return;
        }

        this.$refs.documentGenerator.show(this.patient);
      } catch (error) {
        console.error("Error showing document:", error);
      }
    },

    handleDocumentUploads(e) {
      // For regular document uploads, use the standard polling method
      this.pollForUpdates(e);
    },

    async handleDocumentGenerate(documentType) {
      try {
        this.generatingDocument = documentType;
        this.currentDocumentType = documentType;

        await this.$nextTick();

        if (!this.$refs.documentGenerator) {
          console.warn("Document generator reference not found");
          return;
        }

        this.$refs.documentGenerator.show(this.patient);
      } catch (error) {
        console.error("Error generating document:", error);
      } finally {
        // Reset generating state after a delay to show completion
        setTimeout(() => {
          this.generatingDocument = null;
        }, 1000);
      }
    },

    handleUploadSuccess({ category, count, files = [] }) {
      // Trigger polling to refresh file list
      if (category === "Template") {
        this.pollForUpdatesTemplate(files);
      } else {
        this.pollForUpdates(files);
      }
    },

    handleUploadError(error) {
      console.error("Upload error:", error);
      this.$bvToast.toast("เกิดข้อผิดพลาดในการอัปโหลดไฟล์", {
        title: "อัปโหลดล้มเหลว",
        variant: "danger",
        solid: true,
        autoHideDelay: 5000,
      });
    },

    async handleTemplateSign(templateId) {
      try {
        this.signingTemplate = templateId;

        // Find the template document
        const template = this.templateDocuments.find(t => t.id === templateId);
        if (!template) {
          console.error("Template not found:", templateId);
          return;
        }

        // Use the existing openSignTab method (from GalleryMixin)
        this.openSignTab(template);
      } catch (error) {
        console.error("Error opening template for signing:", error);
      } finally {
        // Reset signing state after a delay
        setTimeout(() => {
          this.signingTemplate = null;
        }, 1000);
      }
    },

    handleTemplateUpload() {
      // Show the upload modal specifically for template documents
      this.showUploadModal("Template");
    },
  },
  mounted() {
    // Fetch both regular files and template files for document mode
    this.fetchMediaFiles({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
      params: { patientId: this.patient.id, limit: this.limit },
    });

    this.fecthMediaFilesTemplate({
      clinicUrl: this.$route.params.clinicUrl,
      branchUrl: this.$route.params.branchUrl,
    });
  },
};
</script>

<style scoped>
.document-viewer {
  padding: 0;
  max-width: 100%;
}

.document-generation-compact {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.compact-actions {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.document-categories {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.document-categories .category-card {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.document-categories .category-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-color: #d1d5db;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .document-generation-compact {
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .compact-actions {
    padding: 0.5rem 0.75rem;
    margin-bottom: 0.75rem;
  }

  .document-categories {
    gap: 0.75rem;
  }
}

@media (max-width: 576px) {
  .document-generation-compact {
    padding: 0.625rem;
    margin-bottom: 0.75rem;
  }

  .compact-actions {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .document-categories {
    gap: 0.5rem;
  }
}
</style>